import { type FC, useState, useMemo, useCallback } from 'react';
import { Alert, Form } from 'react-bootstrap';
import Select from 'react-select';

import { aiChatboxToAiChatboxProperties } from '@editor2/mappers/element';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { elementCommonStyles } from '@editor2/styles/element';
import { type AiChatbox } from '@editor2/types/pageElements';
import {
	type ModelOption,
	toModelOption,
	configToSelectedModels,
	selectedModelsToConfig
} from '@editor2/services/aiModelService';
import { useAiModels } from '@editor2/hooks/useAiModels';
import AiModelsStatus from '@editor2/components/AiModelsStatus';

interface Props {
	element: AiChatbox;
	courseId: number;
}

interface AiChatboxProperties {
	title: string;
	config: string;
}

/** Retired models stay selectable, so strike them through to keep the current ones obvious. */
const formatModelOptionLabel = (option: ModelOption) =>
	option.isDeprecated ? <s>{option.label}</s> : option.label;

const AiChatboxElement: FC<Props> = (props) => {
	const { element, courseId } = props;
	const catalog = useAiModels();

	const [chatboxProperties, setChatboxProperties] = useState<AiChatboxProperties>(
		aiChatboxToAiChatboxProperties(element)
	);
	const [selectionError, setSelectionError] = useState<string | null>(null);
	const debouncedDispatch = useDebouncedDispatch();
	const commonPayload = useMemo(
		() => ({ elementId: element.family_id, courseId }),
		[element.family_id, courseId]
	);

	const handleChange = useCallback(
		(payload: Partial<typeof chatboxProperties>) => {
			setChatboxProperties((props) => ({ ...props, ...payload }));
			debouncedDispatch(updateElement({ ...commonPayload, ...payload }));
		},
		[commonPayload, debouncedDispatch]
	);

	const handleModelSelection = useCallback(
		(selectedOptions: ModelOption[]) => {
			if (!catalog.models || catalog.error || catalog.loading) return;
			if (selectedOptions.length === 0) {
				// The chatbox offers exactly the models saved in `assistants`, so clearing the selection
				// would leave students with nothing to chat with.
				setSelectionError('Select at least one model.');
				return;
			}

			setSelectionError(null);
			const newConfig = selectedModelsToConfig(
				selectedOptions,
				chatboxProperties.config,
				catalog.models
			);
			handleChange({ config: newConfig });
		},
		[chatboxProperties.config, handleChange, catalog.models, catalog.error, catalog.loading]
	);

	const selectedModels = useMemo(
		() => configToSelectedModels(chatboxProperties.config, catalog.models ?? []),
		[chatboxProperties.config, catalog.models]
	);

	const selectedRetiringModels = useMemo(
		() => selectedModels.filter((option) => option.isDeprecated),
		[selectedModels]
	);

	return (
		<Form className={elementCommonStyles}>
			<Form.Group>
				<Form.Label>Title</Form.Label>
				<Form.Control
					value={chatboxProperties.title}
					onChange={(e) => handleChange({ title: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Available Models</Form.Label>
				<AiModelsStatus {...catalog} />
				{selectedRetiringModels.length > 0 && (
					<Alert variant="warning">
						This chatbox offers {selectedRetiringModels.map((option) => option.label).join(', ')},{' '}
						{selectedRetiringModels.length === 1 ? 'which is' : 'which are'} being retired, so it
						may stop working during the term.
					</Alert>
				)}
				<Form.Text className="text-muted mb-2">
					Models shown with a <s>strikethrough</s> are being retired by their provider, and are
					likely to stop working within the timeframe of a school term. Remove them and replace them
					with an appropriate substitute from the same provider to ensure continuity of operation
					throughout the course’s lifespan.
				</Form.Text>
				<Select
					isMulti={true}
					options={(catalog.models ?? []).map((model) => toModelOption(model, catalog.models))}
					value={selectedModels}
					isLoading={catalog.loading}
					isDisabled={!catalog.models || catalog.error || catalog.loading}
					formatOptionLabel={formatModelOptionLabel}
					onChange={handleModelSelection}
					placeholder="Select AI models..."
					closeMenuOnSelect={false}
				/>
				{selectionError && <Form.Text className="text-danger">{selectionError}</Form.Text>}
			</Form.Group>
		</Form>
	);
};

export default AiChatboxElement;
